import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import DashboardContent from "../../components/DashboardContent";
import PatientNarrativeQuestionLabel from "../../components/PatientNarrativeQuestionLabel";
import RadioGroup from "../../components/RadioGroup";
import CheckboxGroupWithOther from "../../components/CheckboxGroupWithOther";
import DetectedValueConfirmation from "../../components/DetectedValueConfirmation";
import Loader from "../../utils/Loader";
import usePatientNarrativeWizardSection from "../../hooks/usePatientNarrativeWizardSection";
import usePatientNarrativeQuestions from "../../hooks/usePatientNarrativeQuestions";
import { usePermissions } from "../../contexts/PermissionsContext";
import { PERMISSIONS } from "../../utils/permissions";
import { PATIENT_NARRATIVE_WIZARD_SECTIONS } from "../../utils/patientNarrativeWizardSections";
import {
  describeSaveError,
  describeLoadError,
} from "../../utils/patientNarrativeErrors";
import questionsData from "../../assets/json/patient_narrative_questions_data.json";

const SECTION = PATIENT_NARRATIVE_WIZARD_SECTIONS.patientScope;

const MANUAL_LIST_METHODS = ["manual", "automatic_plus_manual"];

const PatientScope = () => {
  const { studyId: patientNarrativeId } = useParams();
  const { hasPermission } = usePermissions();

  // pn:wizards:read gates the GET that pre-fills this section; writing it back
  // needs pn:wizards:update on top, so without either the Save button is
  // disabled and Next navigates without submitting.
  const canReadWizards = hasPermission(PERMISSIONS.pnWizardsRead);
  const canSaveWizards =
    canReadWizards && hasPermission(PERMISSIONS.pnWizardsUpdate);
  const {
    answers,
    loading,
    saving,
    error,
    updateFields,
    save,
    errorForField,
    revealErrors,
    hasMissingRequiredFields,
  } = usePatientNarrativeWizardSection(patientNarrativeId, SECTION, {
    canRead: canReadWizards,
    canSave: canSaveWizards,
  });
  const {
    questions,
    loading: questionsLoading,
    error: questionsError,
  } = usePatientNarrativeQuestions();
  const [saveError, setSaveError] = useState("");

  const handleSave = async event => {
    event?.preventDefault?.();
    setSaveError("");
    // Trying to move on is what asks for the outstanding questions to be
    // pointed out - nothing is flagged before this.
    revealErrors();
    try {
      await save();
      return { success: true };
    } catch (err) {
      setSaveError(describeSaveError(err));
      return { success: false };
    }
  };

  // A failed GET leaves no answers to edit - show why instead of the loader.
  if (loading || questionsLoading || !answers) {
    return (
      <DashboardContent
        sidebarCSR={true}
        headerTitle={"Patient Scope"}
        pageContent={
          !loading && error ? (
            <span className={"errmsg"} aria-live="assertive">
              {describeLoadError(error)}
            </span>
          ) : (
            <Loader loading={true} />
          )
        }
      />
    );
  }

  const qualifyingEventsError = errorForField("qQualifyingEvents");
  const otherEventsError = errorForField("other_events");
  const participantListMethodError = errorForField("qParticipantListSource");
  const subjectIdentifierFieldError = errorForField("qSubjectIdField");
  const subjectIdentifierConfirmedError = errorForField(
    "subject_identifier_confirmed"
  );

  const PageContent = (
    <div className={"questions-content pn-patient-scope"}>
      {error && (
        <span className={"errmsg"} aria-live="assertive">
          {describeLoadError(error)}
        </span>
      )}
      {questionsError && (
        <span className={"errmsg"} aria-live="assertive">
          {describeLoadError(questionsError)}
        </span>
      )}
      {saveError && (
        <span className={"errmsg"} aria-live="assertive">
          {saveError}
        </span>
      )}

      <div className={"questions-content__container"}>
        <div className={"questions-content__questionIndexContainer"}>
          <span className={"questions-content__label-index"}>1</span>
        </div>
        <div className={"questions-content__item"}>
          <PatientNarrativeQuestionLabel
            section={SECTION}
            questions={questions}
            field={"qQualifyingEvents"}
          />
          <CheckboxGroupWithOther
            name={"qQualifyingEvents"}
            value={answers.qQualifyingEvents}
            onChange={value => updateFields({ qQualifyingEvents: value })}
            options={questionsData.choices.qQualifyingEvents}
            otherValue={"other"}
            otherTexts={answers.other_events || []}
            onOtherTextsChange={texts => updateFields({ other_events: texts })}
            disabled={saving}
          />
          {qualifyingEventsError && (
            <div className={"completion-error"}>
              {qualifyingEventsError.message}
            </div>
          )}
          {otherEventsError && (
            <div className={"completion-error"}>{otherEventsError.message}</div>
          )}
        </div>
      </div>

      <div className={"questions-content__container"}>
        <div className={"questions-content__questionIndexContainer"}>
          <span className={"questions-content__label-index"}>2</span>
        </div>
        <div className={"questions-content__item"}>
          <PatientNarrativeQuestionLabel
            section={SECTION}
            questions={questions}
            field={"qParticipantListSource"}
          />
          <RadioGroup
            name={"qParticipantListSource"}
            value={answers.qParticipantListSource}
            onChange={value => updateFields({ qParticipantListSource: value })}
            options={questionsData.choices.qParticipantListSource}
            disabled={saving}
          />
          {participantListMethodError && (
            <div className={"completion-error"}>
              {participantListMethodError.message}
            </div>
          )}
          {MANUAL_LIST_METHODS.includes(answers.qParticipantListSource) && (
            <div className={"pn-dependency-note"}>
              Manual participant ID entry is not built yet - this choice is
              saved, but there is nowhere to enter IDs until that feature ships.
            </div>
          )}
        </div>
      </div>

      <div className={"questions-content__container"}>
        <div className={"questions-content__questionIndexContainer"}>
          <span className={"questions-content__label-index"}>3</span>
        </div>
        <div className={"questions-content__item"}>
          <PatientNarrativeQuestionLabel
            section={SECTION}
            questions={questions}
            field={"qSubjectIdField"}
          />
          {/* Detection of a candidate field is not built in the backend yet,
          so there is never a detected value to start from. */}
          <DetectedValueConfirmation
            detectedValue={null}
            fieldValue={answers.qSubjectIdField}
            confirmed={answers.subject_identifier_confirmed === true}
            onChangeField={value => updateFields({ qSubjectIdField: value })}
            onConfirmField={() =>
              updateFields({ subject_identifier_confirmed: true })
            }
            disabled={saving}
          />
          {subjectIdentifierFieldError && (
            <div className={"completion-error"}>
              {subjectIdentifierFieldError.message}
            </div>
          )}
          {subjectIdentifierConfirmedError && (
            <div className={"completion-error"}>
              {subjectIdentifierConfirmedError.message}
            </div>
          )}
        </div>
      </div>
      <ToastContainer
        position="top-right"
        autoClose={2000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick={true}
        rtl={false}
        pauseOnFocusLoss={false}
        draggable
        pauseOnHover
        theme="light"
      />
    </div>
  );

  return (
    <DashboardContent
      sidebarCSR={true}
      headerTitle={"Patient Scope"}
      pageContent={PageContent}
      prevButton={{
        buttonTitle: "Project & Conventions",
        buttonGoToAction: "/pn/" + patientNarrativeId + "/project-conventions",
      }}
      nextButton={{
        buttonTitle: "Sources",
        buttonGoToAction: "/pn/" + patientNarrativeId + "/sources",
        disabled: hasMissingRequiredFields,
      }}
      saveButton={true}
      savePermission={[PERMISSIONS.pnWizardsRead, PERMISSIONS.pnWizardsUpdate]}
      saveButtonHandler={handleSave}
    />
  );
};

export default PatientScope;
